import { useTranslation } from "react-i18next";
import { getIntlLocale } from "@/i18n";
import { Badge } from "@/components/ui";
import {
  formatCalendarDateHeading,
  todayCalendarDateKey,
  type CalendarDateKey,
} from "./bookingCalendar";
import { cn } from "@/lib/utils";

interface BookingsDaySectionHeaderProps {
  dateKey: CalendarDateKey | null;
  bookingCount: number;
  covers: number;
  idPrefix: string;
  className?: string;
}

export function BookingsDaySectionHeader({
  dateKey,
  bookingCount,
  covers,
  idPrefix,
  className,
}: BookingsDaySectionHeaderProps) {
  const { t } = useTranslation("bookings");
  const isToday = dateKey === todayCalendarDateKey();
  const heading = dateKey
    ? formatCalendarDateHeading(dateKey, getIntlLocale())
    : t("list.undatedSection");
  const sectionId = `${idPrefix}-${dateKey ?? "undated"}`;

  return (
    <header
      className={cn(
        "flex flex-wrap items-baseline gap-x-2.5 gap-y-1",
        className,
      )}
      aria-labelledby={sectionId}
    >
      <h3
        id={sectionId}
        className="text-[15px] font-semibold capitalize tracking-tight text-surface-900"
      >
        {heading}
      </h3>
      {isToday ? (
        <Badge variant="primary" size="sm" className="self-center">
          {t("calendar.todayBadge")}
        </Badge>
      ) : null}
      <p className="text-[13px] tabular-nums text-surface-500">
        {covers > 0
          ? t("calendar.daySummaryWithCovers", { count: bookingCount, covers })
          : t("bookingCount", { count: bookingCount })}
      </p>
    </header>
  );
}
